import { TextField } from "@kora/kit/primitives/TextField/TextField";
import { useEffect, useState } from "react";
import { Failure, GhostButton, Loading, Messages, Note, PasswordToggle, PrimaryButton, Screen } from "../components/Entry";
import { RECOVERED, copy } from "../lib/copy";
import { errorsFor, go, useFlow, valueOf } from "../lib/flow";
import { logoutUrl, whoami, type Flow, type Session } from "../lib/kratos";

// Kratos's privileged_session_max_age: a recovery sign-in this recent is still the reset in progress.
const RECOVERY_WINDOW_MS = 15 * 60 * 1000;
const fromRecovery = (session: Session | null) =>
  Boolean(
    session?.authentication_methods?.some(
      m => /_recovery$/.test(m.method) && m.completed_at && Date.now() - Date.parse(m.completed_at) < RECOVERY_WINDOW_MS,
    ),
  );

export function Settings({ query }: { query: URLSearchParams }) {
  const state = useFlow("settings", "/settings", query, { return_to: query.get("return_to") ?? undefined });
  const { flow } = state;
  const [session, setSession] = useState<Session | null>();
  useEffect(() => {
    whoami()
      .then(setSession)
      .catch(() => setSession(null));
  }, [flow?.id]);
  if (!flow || session === undefined) return state.failure ? <Screen title="Account"><Failure text={state.failure} /></Screen> : <Loading />;
  // A reset link lands here with a short privileged session: only the new password matters. Kratos drops its
  // recovery message when the emailed second factor comes first, so the session's sign-in method decides too.
  const recovering = (flow.ui.messages ?? []).some(m => m.id === RECOVERED) || fromRecovery(session);
  return recovering ? <NewPassword flow={flow} state={state} /> : <Account flow={flow} state={state} />;
}

type State = ReturnType<typeof useFlow>;

function PasswordFields({ flow, state, onSaved }: { flow: Flow; state: State; onSaved?: () => void }) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [shown, setShown] = useState(false);
  const [mismatch, setMismatch] = useState(false);
  return (
    <form
      noValidate
      className="entry-form"
      onSubmit={event => {
        event.preventDefault();
        if (password !== confirmation) return setMismatch(true);
        setMismatch(false);
        void state.submit({ method: "password", password }, () => onSaved?.());
      }}
    >
      <TextField
        label="New password"
        inputType={shown ? "text" : "password"}
        inputProps={{ autoComplete: "new-password", name: "password" }}
        value={password}
        onChange={setPassword}
        error={errorsFor(flow, "password").map(copy)[0]}
        size="lg"
      />
      <TextField
        label="Confirm new password"
        inputType={shown ? "text" : "password"}
        inputProps={{ autoComplete: "new-password" }}
        value={confirmation}
        onChange={value => {
          setConfirmation(value);
          setMismatch(false);
        }}
        error={mismatch ? "Those passwords don’t match. Try again." : undefined}
        size="lg"
      />
      <PasswordToggle shown={shown} onToggle={() => setShown(!shown)} />
      <PrimaryButton pending={state.pending}>Save new password</PrimaryButton>
    </form>
  );
}

function NewPassword({ flow, state }: { flow: Flow; state: State }) {
  if (flow.state === "success") {
    return (
      <Screen title="Password saved">
        <form
          noValidate
          className="entry-form"
          onSubmit={event => {
            event.preventDefault();
            go(flow.return_to || "/");
          }}
        >
          <Note>Your new password is set.</Note>
          <PrimaryButton>Continue</PrimaryButton>
        </form>
      </Screen>
    );
  }
  return (
    <Screen title="Set new password">
      <Messages messages={(flow.ui.messages ?? []).filter(m => m.type === "error")} />
      <Failure text={state.failure} />
      <PasswordFields flow={flow} state={state} />
    </Screen>
  );
}

function Account({ flow, state }: { flow: Flow; state: State }) {
  const signOut = async () => go((await logoutUrl(window.location.origin + "/login")) ?? "/login");
  return (
    <Screen title="Account">
      <div className="entry-form">
        <Note>Signed in as {valueOf(flow, "traits.email")}</Note>
        <Messages messages={flow.ui.messages ?? []} />
        <Failure text={state.failure} />
      </div>
      <h2 className="text-label portal-section">Change password</h2>
      <PasswordFields flow={flow} state={state} />
      <div className="entry-form portal-section">
        {flow.return_to && <GhostButton onClick={() => go(flow.return_to!)}>Back</GhostButton>}
        <GhostButton onClick={() => void signOut()}>Sign out</GhostButton>
      </div>
    </Screen>
  );
}
